import { useMemo } from "react";
import { readUsageRecord } from "@addressium/domain";
import { useAsync } from "../useAsync.js";
import { Kpi } from "../Kpi.js";
import { api, type UsageRecord } from "../api.js";
import { SkeletonCard } from "../Skeleton.js";

const usd = (n: number) => `$${n.toFixed(2)}`;
const gb = (bytes: number) => `${(bytes / 1_073_741_824).toFixed(2)} GB`;

const NOT_MEASURED = "Not measured: no figure has been supplied for this period by the operator's AWS metering job";

/**
 * One cost cell: the amount, or an em dash when the line was not measured.
 *
 * A $0.00 implies the charge was measured and came to nothing (#370, #321).
 * Storage, dedicated IPs and Athena are only known once the operator's own
 * metering job has fed them in, and until then they are absent, not zero.
 */
function CostCell({ value, title }: { value: number | undefined; title?: string }) {
  if (value === undefined) return <td title={NOT_MEASURED}>— <span className="muted">not measured</span></td>;
  return <td title={title}>{usd(value)}</td>;
}

/**
 * Usage & cost (#26, #370).
 *
 * Every figure is an ESTIMATE: our own volume counts multiplied by configured
 * rates, not a reading of the AWS bill. The product owner's call on #370 was to
 * treat it as a rough indicator and say so, so the heading, the total and the
 * note below all carry the word.
 */
export function Usage({ org }: { org: string }) {
  const { data, error, loading } = useAsync(() => api.usage(org), [org]);
  const rows = useMemo(() => {
    const list: UsageRecord[] = Array.isArray(data) ? data : data ? [data] : [];
    // The API already applies the legacy rule; applying it here too keeps a row
    // from an older API from printing $0.00 for a line nobody measured (#370).
    return list.map(readUsageRecord).sort((a, b) => b.period.localeCompare(a.period));
  }, [data]);
  const latest = rows[0];
  return (
    <div>
      <h1 className="h1">Estimated usage &amp; cost · {org || "—"}</h1>
      <p className="muted">
        Estimates for chargeback, worked out from our own counts and set rates, not from your AWS bill. Lines marked
        "not measured" have no figure yet and are left out of the estimated total.
        {/* Metered once a day, so a send made today is not in it yet (#370). */}
        {" "}Emails sent are counted once a day at 04:00 UTC{latest?.computedAt ? `; last counted ${new Date(latest.computedAt).toLocaleString()}` : ""}, so today&rsquo;s sends appear tomorrow.
      </p>
      {loading && <SkeletonCard lines={3} />}
      {error && <p className="err">{error}</p>}
      {!loading && !error && rows.length === 0 && (
        <div className="card muted">No usage recorded yet. Metering populates once the scheduled job has run for a period.</div>
      )}
      {latest && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 8 }}>Latest period · {latest.period}</div>
          <div className="kpis">
            <Kpi n={usd(latest.cost.total)} l="estimated total" />
            <Kpi n={latest.emailsSent} l="emails sent" />
            <Kpi
              n={latest.athenaBytesScanned === undefined ? "—" : Number(gb(latest.athenaBytesScanned).split(" ")[0])}
              l={latest.athenaBytesScanned === undefined ? "GB scanned (Athena) · not measured" : "GB scanned (Athena)"}
            />
            <Kpi
              n={latest.dedicatedIps ?? "—"}
              l={latest.dedicatedIps === undefined ? "dedicated IPs · not measured" : "dedicated IPs"}
            />
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 8 }}>Estimated cost by period (email · storage · dedicated IP · Athena scan · backup)</div>
          <table>
            <thead>
              <tr><th>Period</th><th>Email</th><th>Storage</th><th>Ded. IP</th><th>Athena</th><th>Backup</th><th>Estimated total</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.period}>
                  <td>{r.period}</td>
                  <td>{usd(r.cost.email)}</td>
                  <CostCell value={r.cost.storage} />
                  <CostCell value={r.cost.dedicatedIp} />
                  <CostCell value={r.cost.athena} title={r.athenaBytesScanned === undefined ? undefined : gb(r.athenaBytesScanned)} />
                  {/* An em dash, not $0.00, when backups are off. A zero implies
                      the charge was measured and came to nothing; this
                      deployment simply does not take them (#321). */}
                  <td title={r.cost.backup === undefined ? "AWS Backup is not enabled for this deployment" : undefined}>
                    {r.cost.backup === undefined ? "—" : usd(r.cost.backup)}
                  </td>
                  <td className="t-strong">{usd(r.cost.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
